import { ReactNode } from 'react';

export enum VirtualizedTableColType {
  String = 'string',
  Number = 'number',
  Mulitselect = 'multiselect',
  Date = 'date',
}

export enum VirtualizedTableSelectionType {
  None = 'none',
  Multi = 'multi',
  Single = 'single',
}

export interface VirtualizedTableRowAction {
  icon: JSX.Element;
  onClick: (rowKey: string | number) => void;
  tooltip?: string;
  isDisabled?: (rowKey: string | number) => boolean;
  ariaLabel: string | undefined;
}

/**
 * Defines the columns of a table
 */
export interface VirtualizedTableColumnProps {
  /**
   * Column caption
   */
  header: React.ReactNode;
  /**
   * Allow to sort bi-directional on click on column header.
   */
  sorting: boolean;
  /**
   * Defines the type of the displayed data (only applies if
   * inline editing is allowed)
   */
  type: VirtualizedTableColType;
  /**
   * Allow inline editing via doubleclick on rows for this column.
   */
  editable: boolean;
  /**
   * Width of the column in px
   */
  width?: number;
}

export type VirtualizedTableKey = string | number;

export interface VirtualizedTableRowProps {
  key: VirtualizedTableKey;
  cells: VirtualizedTableCellProps[];
  icon?: JSX.Element;
  /**
   * Set to false, to prevent selection. If unset or undefined the
   * row may be selected.
   */
  isSelectable?: boolean;
  isEditable?: boolean;
}

export interface VirtualizedTableCellProps {
  key: VirtualizedTableKey;
  value: string | ReactNode;
  onClick?: (rowKey: VirtualizedTableKey, colKey: VirtualizedTableKey) => void;
  showTooltip?: boolean;
}

/**
 * Defined how to fit all the table columns.
 */
export enum VirtualizedTableColumnFit {
  /**
   * If all columns are wider than the available
   * horizontal space, a horizontal scrollbar is shown.
   */
  Scroll = 'scroll',
  /**
   * All columns are fit into the available width; no
   * horizontal scrollbar is shown.
   */
  Fit = 'fit',
}
/**
 *
 *
 * @export
 * @interface VirtualizedTableProps
 */
export interface VirtualizedTableProps {
  /**
   * Defined the table's columns
   */
  columns: VirtualizedTableColumnProps[];
  /**
   * The table data
   */
  rows: VirtualizedTableRowProps[];
  /**
   * Defines the selection model of the table
   */
  selectionType?: VirtualizedTableSelectionType;
  /**
   * Gets Called after Cancel button hitted
   */

  onRowEditCancel?: (rowKey: VirtualizedTableKey) => void;
  /**
   * Event handler for inline edited rows
   */
  onRowEdited?: (
    rowKey: VirtualizedTableKey,
    editedCell: VirtualizedTableCellProps[],
  ) => boolean | void;
  /**
   * Gets called, before a row becomes available for editing after
   * an user double clicked on it.
   */
  onBeforeRowEdited?: (rowKey: VirtualizedTableKey) => void;
  /**
   * Action buttons to be displayed at the end of each row
   */
  actions?: VirtualizedTableRowAction[];
  /**
   * Gets called if the user changes the row selection
   */
  onRowSelectionChange?: (selectedRows: VirtualizedTableRowProps[]) => void;
  /**
   * Defines how the columns should align.
   */
  columnFit?: VirtualizedTableColumnFit;
  /**
   * Row ikeys that are pre-selected on the first render of the
   * table.
   */
  defaultSelectedRows?: VirtualizedTableKey[];
  /**
   * Defines the class name of the table
   */
  className?: string;
  /**
   * If multiselection ís required, we will trigger this function on EDIT
   */
  onMultiSelect?: (rowKey: VirtualizedTableKey, cellIndex: number) => void;
  /**
   * Defines which rows of the table are selected.
   * Implicitly determines whether the selection of rows in the table is controlled externally.
   */
  selected?: VirtualizedTableKey[];
  /**
   * Defines how many rows should be rendered at the same time.
   * Is used to set the maximum height of the table if not enough space is available.
   */
  maxSimultaneouslyRows?: number;
  /**
   * Gets called, when the sort direction or column to sort by
   * changes (ie. the user clicked on a table column header).
   */
  onSortChange?: (sort: VirtualizedTableSort) => void;
  /**
   * Total number of rows in the table.
   * This is used to determine the total number of rows for infinite loading.
   */
  rowsTotalCount?: number;
  /**
   * Function to determine if more rows should be loaded.
   * Used for infinite loading.
   */
  onLoadMoreItems?: (
    startIndex: number,
    stopIndex: number,
  ) => void | Promise<void>;
  /**
   * Default sort to be applied on the table.
   */
  defaultSort?: VirtualizedTableSort;
}

export enum VirtualizedTableSortDirection {
  Asc = 'asc',
  Desc = 'desc',
}

export interface VirtualizedTableSort {
  direction: VirtualizedTableSortDirection;
  column: number;
}
